/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Tag, Spin } from '@arco-design/web-react';
import { RootState } from '@/store';
import Loading from '@/components/Loading/HorizonLoading';
import { isMobile } from '@/utils/utils';
import { useScene } from '@/lib/useCommon';
import styles from './index.module.less';
import AIAvatarReadying from '@/components/AIAvatarLoading';

const lines: (string | React.ReactNode)[] = [];

type ConversationProps = React.HTMLAttributes<HTMLDivElement> & {
  showSubtitle: boolean;
  maxMessages?: number;
  showLoadingIndicator?: boolean;
  showInterruptedTag?: boolean;
  /** Restrict which side is rendered so AI / user subtitles can live in
   *  separately-positioned bubbles. Omit to render both (default). */
  roleFilter?: 'ai' | 'user';
};

function Conversation(props: ConversationProps) {
  const {
    className,
    showSubtitle,
    maxMessages = 3,
    showLoadingIndicator = true,
    showInterruptedTag = true,
    roleFilter,
    ...rest
  } = props;
  const room = useSelector((state: RootState) => state.room);
  const { msgHistory, isFullScreen } = room;
  const { userId } = useSelector((state: RootState) => state.room.localUser);
  const { isAITalking, isUserTalking } = useSelector((state: RootState) => state.room);
  // Some scenes may not produce subtitles immediately (e.g. before the user speaks).
  // Treat the AI as "ready" once AIGC has been enabled, even if msgHistory is still empty.
  const isAIReady = room.isAIGCEnable || msgHistory.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const { botName, isAvatarScene, name } = useScene();
  const aiDisplayName = name || botName || 'AI';
  const visibleMsgs = (showSubtitle ? msgHistory : []).filter(({ user }) => {
    const isUserMsg = user === userId;
    const isAiMsg = user === botName || user.includes('voiceChat_');
    if (!isUserMsg && !isAiMsg) return false;
    if (roleFilter === 'ai') return isAiMsg;
    if (roleFilter === 'user') return isUserMsg;
    return true;
  });
  const recentMsgs = visibleMsgs.slice(-maxMessages);
  // The "AI 准备中" placeholder belongs only to the AI side; never show it in a
  // user-only bubble.
  const showReadyingPlaceholder = roleFilter !== 'user';

  const isUserTextLoading = (owner: string) => {
    return owner === userId && isUserTalking;
  };

  const isAITextLoading = (owner: string) => {
    return (owner === botName || owner.includes('voiceChat_')) && isAITalking;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
    }
  }, [msgHistory.length]);

  return (
    <div
      ref={containerRef}
      className={`${styles.conversation} ${className} ${isFullScreen ? styles.fullScreen : ''} ${
        isMobile() ? styles.mobileConversation : ''
      }`}
      style={isAvatarScene && !isAIReady ? { justifyContent: 'center' } : {}}
      {...rest}
    >
      {lines.map((line) => line)}
      {showReadyingPlaceholder && !isAIReady ? (
        <div className={styles.aiReadying}>
          {isAvatarScene ? (
            <AIAvatarReadying />
          ) : (
            <>
              <Spin size={16} className={styles['aiReading-spin']} />
              AI 准备中, 请稍侯
            </>
          )}
        </div>
      ) : showReadyingPlaceholder && msgHistory.length === 0 ? (
        <div className={styles.aiReadying}>{isAvatarScene ? <AIAvatarReadying /> : null}</div>
      ) : null}
      {recentMsgs.map(({ value, user, isInterrupted }, index) => {
        const isUserMsg = user === userId;
        return (
          <div
            key={`msg-container-${index}`}
            className={styles.mobileLine}
            style={{ justifyContent: isUserMsg && isMobile() ? 'flex-end' : '' }}
          >
            <div
              className={`${styles.sentence} ${isUserMsg ? styles.user : styles.robot}`}
              key={`msg-${index}`}
            >
              <div className={styles.inlineName}>{isUserMsg ? '你' : aiDisplayName}</div>
              <div className={styles.content}>
                {value}
                <div className={styles['loading-wrapper']}>
                  {showLoadingIndicator &&
                  isAIReady &&
                  (isUserTextLoading(user) || isAITextLoading(user)) &&
                  index === recentMsgs.length - 1 ? (
                    <Loading gap={3} className={styles.loading} dotClassName={styles.dot} />
                  ) : (
                    ''
                  )}
                </div>
              </div>
              {!isUserMsg && isInterrupted && showInterruptedTag ? (
                <Tag className={styles.interruptTag}>已打断</Tag>
              ) : (
                ''
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Conversation;
